import * as path from 'path';
import fs from 'fs-extra';
import { StaticAdapter } from './StaticAdapter.js';
import { BuildContext } from './BaseAdapter.js';
import { ConfigLoader } from '../config/ConfigLoader.js';

/**
 * Deploy target for Bodify (self-hosted Vercel-alt). A Frame app is a static SPA, so this
 * extends StaticAdapter (content-hashed assets + SPA fallback) and additionally:
 *   1. emits a `bodify.yaml` (app id/domain, prerender routes auto-discovered from the router),
 *   2. injects the `/_bodify/sdk.js` client SDK into the built index.html,
 *   3. skips the Netlify/Vercel/Apache files (irrelevant on Bodify).
 *
 * Configure via `bodify:` in frame.config.mjs (all optional):
 *   bodify: {
 *     appId, name, domain,
 *     sdk: true,                 // inject /_bodify/sdk.js + analytics (default true)
 *     analytics: true,
 *     prerender: true | { humanPaths: 'auto' | string[], ttl, edge },
 *   }
 */
export class BodifyAdapter extends StaticAdapter {
	constructor() {
		super({ spa: true, fallback: 'index.html' });
		(this as any).name = 'Bodify';
	}

	private async loadBodifyConfig(cwd: string): Promise<any> {
		const loader = new ConfigLoader(cwd);
		const cfg = await loader.load();
		return { bodify: (cfg as any).bodify || {}, full: cfg };
	}

	/** Parse concrete (non-param) route paths from the generated router for prerendering. */
	private discoverRoutes(cwd: string): string[] {
		const routesPath = path.join(cwd, 'src', '.frame', 'routes.ts');
		if (!fs.existsSync(routesPath)) return ['/'];
		const src = fs.readFileSync(routesPath, 'utf-8');
		const paths = Array.from(src.matchAll(/path:\s*'([^']+)'/g)).map(m => m[1]);
		const concrete = paths.filter(p => p && !p.includes(':') && !p.includes('*'));
		return concrete.length ? Array.from(new Set(concrete)) : ['/'];
	}

	async processBuild(context: BuildContext): Promise<void> {
		await super.processBuild(context);
		const { bodify } = await this.loadBodifyConfig(context.cwd);
		if (bodify.sdk !== false) await this.injectSdk(context, bodify);
	}

	/** Inject the Bodify client SDK (analytics) before the entry module in the built index.html. */
	private async injectSdk(context: BuildContext, bodify: any): Promise<void> {
		const indexPath = path.join(context.cwd, context.outDir, 'index.html');
		if (!await fs.pathExists(indexPath)) return;
		let html = await fs.readFile(indexPath, 'utf-8');
		if (html.includes('/_bodify/sdk.js')) return;
		const appId = bodify.appId || bodify.name || path.basename(context.cwd);
		// Encode "off" EXPLICITLY: the SDK auto-inits on data-app-id alone and defaults
		// analytics ON, reading OFF only from data-analytics="false". Omitting the attribute
		// would be read as ON — so disabled must emit the literal false the SDK expects.
		const analyticsOff = bodify.analytics === false;
		const analytics = analyticsOff ? ' data-analytics="false"' : ' data-analytics';
		// For normal bodify-served web builds we OMIT data-ingest-url and let the SDK
		// self-derive it from its own <script> origin (same host as the page). For
		// bundled/offline targets (appwrap loader:'app') the page AND sdk.js load from a
		// local scheme (capacitor://, file://, localhost), so same-origin derivation would
		// POST into the void — inject an absolute bodify ingest URL at build time.
		const ingestUrl = this.resolveIngestUrl(bodify, appId);
		const ingestAttr = ingestUrl ? ` data-ingest-url="${ingestUrl}"` : '';
		const tag = `<script src="/_bodify/sdk.js" data-app-id="${appId}"${ingestAttr}${analytics} defer></script>`;
		html = html.includes('</body>') ? html.replace('</body>', `  ${tag}\n</body>`) : html + tag;
		await fs.writeFile(indexPath, html);
		this.log(`Injected /_bodify/sdk.js into index.html${ingestUrl ? ` (ingestUrl=${ingestUrl})` : ''}`);
		// Bundled/offline-wrapped apps load the page AND sdk.js from a local scheme, so
		// SDK self-derivation posts into the void. Without an absolute URL we can't tell a
		// normal web build from a wrapped one — warn so offline authors set bodify.offline.
		if (!analyticsOff && !ingestUrl) {
			this.log('WARNING: injected analytics SDK without an absolute ingest URL — offline/bundled-wrapped apps must set bodify.offline or bodify.ingestUrl or analytics will not reach bodify.');
		}
	}

	/**
	 * Absolute ingest URL to embed, or null to let the SDK self-derive (normal web).
	 * Returned only for bundled/offline builds where the runtime origin is local:
	 * explicit `bodify.ingestUrl` wins; otherwise derived from `bodify.domain`
	 * (falling back to the central bodify host, which routes by appId).
	 */
	private resolveIngestUrl(bodify: any, appId: string): string | null {
		if (bodify.ingestUrl) return bodify.ingestUrl;
		if (!bodify.offline) return null;
		const domain = bodify.domain || 'bodify.bod.ee';
		const origin = /^https?:\/\//.test(domain) ? domain : `https://${domain}`;
		return `${origin.replace(/\/+$/, '')}/api/apps/${appId}/ingest`;
	}

	/** Emit bodify.yaml (does not overwrite an existing one — that is the source of truth). */
	async generateDeploymentFiles(context: BuildContext): Promise<void> {
		const yamlPath = path.join(context.cwd, 'bodify.yaml');
		if (await fs.pathExists(yamlPath)) {
			this.log('bodify.yaml already exists — leaving it as the source of truth');
			return;
		}
		const { bodify } = await this.loadBodifyConfig(context.cwd);
		const appId = bodify.appId || bodify.name || path.basename(context.cwd);
		const routes = this.discoverRoutes(context.cwd);

		const lines: string[] = [
			`name: ${bodify.name || appId}`,
			`appId: ${appId}`,
			`instance: ${bodify.instance || 'local'}`,
			`deploy: upload`,
		];
		if (bodify.domain) lines.push(`domain: ${bodify.domain}`);

		const pr = bodify.prerender;
		if (pr) {
			lines.push('', 'prerender:');
			lines.push('  routes:');
			for (const r of routes) lines.push(`    - ${r}`);
			const human = pr === true ? undefined : pr.humanPaths;
			const humanPaths = human === 'auto' ? routes : Array.isArray(human) ? human : undefined;
			if (humanPaths) {
				lines.push('  humanPaths:');
				for (const r of humanPaths) lines.push(`    - ${r}`);
			}
			if (pr !== true && pr.ttl) lines.push(`  ttl: ${pr.ttl}`);
			if (pr !== true && pr.edge) lines.push(`  edge: ${pr.edge}`);
		}
		if (bodify.analytics !== false) lines.push('', 'analytics: true');

		await fs.writeFile(yamlPath, lines.join('\n') + '\n');
		this.log(`Generated bodify.yaml (appId=${appId}, ${routes.length} prerender routes)`);
	}
}
